<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import CoreIcon from '@core/core-icon.vue';
import CoreLoader from '@core/core-loader.vue';

export interface PopupButton {
	text: string;
	handler?: () => void;
	primary?: boolean;
}

const props = withDefaults(defineProps<{
	closable?: boolean;
	iframe?: string;
}>(), {
	closable: true,
	iframe: '',
});

const emit = defineEmits<{
	(e: 'close'): void;
}>();

const isOpen = ref(false);

const open = () => {
	isOpen.value = true;
};

const close = () => {
	if (!props.closable) return;
	isOpen.value = false;
	emit('close');
};

const forceClose = () => {
	isOpen.value = false;
	emit('close');
};

const isIframe = computed(() => !!props.iframe);
const iframeLoading = ref(true);

const onIframeLoad = () => {
	iframeLoading.value = false;
};

watch(isOpen, (val) => {
	if (val && isIframe.value) {
		iframeLoading.value = true;
	}
});

defineExpose({ open, close, forceClose });
</script>

<template>
	<teleport to="body">
		<transition name="popup-transition">
			<div v-if="isOpen" class="popup" :class="{ 'popup--iframe': isIframe }" @click.self="close">
				<div class="popup__window">
					<button v-if="closable" class="popup__close" @click="close">
						<core-icon name="close-circle-fill" viewBox="0 0 16 16" />
					</button>

					<template v-if="isIframe">
						<core-loader v-if="iframeLoading" absolute class="popup__loader" />
						<iframe
							:src="iframe"
							class="popup__iframe"
							:class="{ 'popup__iframe--hidden': iframeLoading }"
							allow="payment; clipboard-write"
							@load="onIframeLoad"
						/>
					</template>
					<template v-else>
						<slot name="header" />

						<div class="popup__content">
							<slot />
						</div>
					</template>
				</div>
			</div>
		</transition>
	</teleport>
</template>

<style lang="scss">
.popup-transition-enter-active,
.popup-transition-leave-active {
	transition: opacity 0.2s ease;

	.popup__window {
		transition: transform 0.2s ease;
	}
}

.popup-transition-enter-from,
.popup-transition-leave-to {
	opacity: 0;

	.popup__window {
		transform: scale(0.95);
	}
}

.popup {
	position: fixed;
	inset: 0;
	z-index: 999;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1.6rem;
	backdrop-filter: blur(22px);
	-webkit-backdrop-filter: blur(22px);
	background-color: rgba(0, 0, 0, 0.25);
	background-image: linear-gradient(rgba(115, 93, 207, 0.25), rgba(115, 93, 207, 0.25));

	&__window {
		position: relative;
		background-color: rgba(255, 255, 255, 0.95);
		border-radius: 3.2rem;
		width: 100%;
		max-width: 48rem;
		overflow: hidden;

		@include theme-dark {
			background-color: rgba(30, 30, 30, 0.95);
		}
	}

	&__close {
		position: absolute;
		top: 0;
		right: 0;
		width: 6.4rem;
		height: 6.4rem;
		display: flex;
		align-items: center;
		justify-content: center;
		opacity: 0.75;
		cursor: pointer;
		background: none;
		border: none;
		padding: 1.3rem 1.4rem;
		z-index: 1;

		svg {
			width: 3.2rem;
			height: 3.2rem;
			fill: var(--color-text);
		}

		&:hover {
			opacity: 1;
		}
	}

	&__content {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 3.2rem;
		padding: 6.4rem 3.2rem;
	}

	&__icon {
		width: 4.2rem;
		height: 4.2rem;
	}

	&__loader {
		margin-bottom: 0;
		--loader-size: 6rem;
		--loader-width: 4px;
		--loader-color: var(--color-active);
	}

	&__status-icon {
		width: 6rem;
		height: 6rem;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;

		svg {
			width: 2.4rem;
			height: 2.4rem;
		}

		&--success {
			background-color: var(--color-purple);
			fill: var(--color-white);
		}

		&--error {
			background-color: var(--color-red);
			fill: var(--color-white);
		}
	}

	&__text {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1.2rem;
		text-align: center;
	}

	&__title {
		@include font-24-pro-bold;
		color: var(--color-text);

		@include min-desktop {
			font-size: 2.8rem;
		}
	}

	&__subtitle {
		@include font-18-pro-medium;
		color: var(--color-text);
		opacity: 0.5;
	}


	&__buttons {
		display: flex;
		flex-direction: column;
		gap: 1.2rem;
		width: 100%;

		&--row {
			flex-direction: row;
		}
	}

	&__iframe {
		width: 100%;
		height: 100%;
		border: none;
		display: block;

		&--hidden {
			opacity: 0;
		}
	}

	&--iframe {
		padding: 0;

		.popup__window {
			max-width: 80vw;
			height: 80vh;
			border-radius: 2.4rem;

			@include max-desktop {
				max-width: 100%;
				height: 100%;
				border-radius: 0;
			}
		}

		.popup__close {
			background-color: rgba(0, 0, 0, 0.3);
			border-radius: 50%;
			top: 1.2rem;
			right: 1.2rem;
			width: 4rem;
			height: 4rem;
			padding: 0;

			svg {
				width: 2.4rem;
				height: 2.4rem;
				fill: #fff;
			}

			@include max-desktop {
				top: 0.8rem;
				right: 0.8rem;
			}
		}
	}

	&__btn {
		width: 100%;
		height: 6.2rem;
		border-radius: 10rem;
		border: none;
		cursor: pointer;
		@include font-18-pro-medium;

		// Та же поверхность, что у button-primary: одна главная кнопка на проект
		&--primary {
			@include button-primary-surface;
			font-weight: 500;
		}

		&--secondary {
			color: var(--color-text);
			background-color: rgba(5, 5, 5, 0.06);
			transition: opacity ease 0.2s;

			&:hover {
				opacity: 0.85;
			}

			@include theme-dark {
				background-color: rgba(255, 255, 255, 0.1);
			}
		}
	}
}
</style>
